import { supabase } from '../lib/supabase';

// ─── Constants ────────────────────────────────────────────────────────────────
const EXPIRY_SOON_DAYS = 30;
const EXPIRY_CRITICAL_DAYS = 7;
const STALE_SNAG_DAYS = 7;

// Required document categories per site
const REQUIRED_CATEGORIES = ['rams', 'insurance', 'training', 'plant'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const daysDiff = (dateStr) => {
  if (!dateStr) return null;
  const now = new Date();
  now?.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target?.setHours(0, 0, 0, 0);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
};

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const now = new Date();
  now?.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target?.setHours(0, 0, 0, 0);
  return Math.ceil((now - target) / (1000 * 60 * 60 * 24));
};

// ─── Main Service ─────────────────────────────────────────────────────────────
export const complianceService = {
  /**
   * Fetch all raw compliance data in one batch — enriched for AI analysis
   */
  async fetchComplianceData() {
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const sevenDaysAgo = new Date(Date.now() - STALE_SNAG_DAYS * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString();
      const thirtyDaysAgoDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

      const [sitesRes, docsRes, snagsRes, auditsRes, tasksRes, incidentsRes, activitiesRes] = await Promise.all([
        supabase?.from('sites')?.select('id, site_name, compliance_status, status')?.neq('status', 'archived'),
        // NSherjFh PR 2: compliance roll-up excludes soft-deleted docs.
        supabase?.from('documents')?.select('id, site_id, title, category, status, expiry_date, days_until_expiry, owner')?.is('deleted_at', null),
        // NSherjFh PR 4: compliance roll-up excludes soft-deleted snags.
        supabase?.from('snags')?.select('id, site_id, title, description, status, priority, created_at, resolved_at, created_by')?.is('deleted_at', null),
        // NSherjFh PR 7: compliance roll-up excludes soft-deleted audits.
        supabase?.from('site_audits')?.select('id, site_id, status, audit_date')?.is('deleted_at', null)?.order('audit_date', { ascending: false }),
        supabase?.from('daily_tasks')?.select('id, site_id, status, required, task_date, title, category')?.gte('task_date', thirtyDaysAgoDate),
        // NSherjFh PR 5: compliance roll-up excludes soft-deleted incidents.
        supabase?.from('incidents')?.select('id, site_id, severity_level, created_at, incident_type, reported_by, description')?.gte('created_at', thirtyDaysAgo)?.is('deleted_at', null),
        supabase?.from('activities')?.select('id, site_id, activity_type, title, description, severity_level, related_entity_id, created_at, user_id')?.gte('created_at', thirtyDaysAgo)?.order('created_at', { ascending: false })?.limit(200),
      ]);

      if (sitesRes?.error) throw sitesRes?.error;

      return {
        data: {
          sites: sitesRes?.data || [],
          documents: docsRes?.data || [],
          snags: snagsRes?.data || [],
          audits: auditsRes?.data || [],
          tasks: tasksRes?.data || [],
          incidents: incidentsRes?.data || [],
          activities: activitiesRes?.data || [],
          today,
          sevenDaysAgo,
        },
        error: null,
      };
    } catch (err) {
      return { data: null, error: { message: err?.message } };
    }
  },

  /**
   * Build compliance alerts from raw data
   */
  buildAlerts({ documents, snags, audits, tasks, sites, today, sevenDaysAgo }) {
    const alerts = [];

    const siteMap = Object.fromEntries((sites || [])?.map(s => [s?.id, s?.site_name]));

    (documents || [])?.forEach(doc => {
      const siteName = siteMap?.[doc?.site_id] || 'Unknown Site';
      if (!doc?.expiry_date) {
        alerts?.push({
          id: `doc-no-expiry-${doc?.id}`,
          type: 'missing_expiry',
          severity: 'warning',
          title: 'Missing expiry date',
          description: `"${doc?.title}" on ${siteName} has no expiry date set.`,
          siteId: doc?.site_id,
          siteName,
          link: '/document-management',
        });
        return;
      }

      const daysLeft = daysDiff(doc?.expiry_date);
      if (daysLeft === null) return;

      if (daysLeft < 0) {
        alerts?.push({
          id: `doc-expired-${doc?.id}`,
          type: 'expired_document',
          severity: 'critical',
          title: 'Expired document',
          description: `"${doc?.title}" on ${siteName} expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago.`,
          siteId: doc?.site_id,
          siteName,
          link: '/document-management',
        });
      } else if (daysLeft <= EXPIRY_CRITICAL_DAYS) {
        alerts?.push({
          id: `doc-critical-${doc?.id}`,
          type: 'expiring_critical',
          severity: 'critical',
          title: 'Document expiring in \u22647 days',
          description: `"${doc?.title}" on ${siteName} expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}.`,
          siteId: doc?.site_id,
          siteName,
          link: '/document-management',
        });
      } else if (daysLeft <= EXPIRY_SOON_DAYS) {
        alerts?.push({
          id: `doc-soon-${doc?.id}`,
          type: 'expiring_soon',
          severity: 'warning',
          title: 'Document expiring soon',
          description: `"${doc?.title}" on ${siteName} expires in ${daysLeft} days.`,
          siteId: doc?.site_id,
          siteName,
          link: '/document-management',
        });
      }
    });

    const docsBySite = {};
    (documents || [])?.forEach(doc => {
      if (!docsBySite?.[doc?.site_id]) docsBySite[doc.site_id] = new Set();
      const daysLeft = doc?.expiry_date ? daysDiff(doc?.expiry_date) : 0;
      if (daysLeft === null || daysLeft >= 0) {
        docsBySite?.[doc?.site_id]?.add(doc?.category);
      }
    });

    (sites || [])?.forEach(site => {
      const siteCategories = docsBySite?.[site?.id] || new Set();
      const missing = REQUIRED_CATEGORIES?.filter(cat => !siteCategories?.has(cat));
      if (missing?.length > 0) {
        alerts?.push({
          id: `missing-docs-${site?.id}`,
          type: 'missing_documents',
          severity: 'warning',
          title: 'Missing required documents',
          description: `${site?.site_name} is missing: ${missing?.map(c => c?.toUpperCase())?.join(', ')}.`,
          siteId: site?.id,
          siteName: site?.site_name,
          link: '/document-management',
        });
      }
    });

    (snags || [])?.forEach(snag => {
      if (snag?.status === 'resolved') return;
      const age = daysSince(snag?.created_at);
      if (age !== null && age > STALE_SNAG_DAYS) {
        const siteName = siteMap?.[snag?.site_id] || 'Unknown Site';
        alerts?.push({
          id: `snag-stale-${snag?.id}`,
          type: 'stale_snag',
          severity: snag?.priority === 'critical' || snag?.priority === 'high' ? 'critical' : 'warning',
          title: 'Unresolved snag >7 days',
          description: `A ${snag?.priority} priority snag on ${siteName} has been open for ${age} days.`,
          siteId: snag?.site_id,
          siteName,
          link: '/snags',
        });
      }
    });

    const auditsBySite = {};
    (audits || [])?.forEach(audit => {
      if (!auditsBySite?.[audit?.site_id]) auditsBySite[audit.site_id] = [];
      auditsBySite?.[audit?.site_id]?.push(audit);
    });

    (sites || [])?.forEach(site => {
      const siteAudits = auditsBySite?.[site?.id] || [];
      const completedRecently = siteAudits?.some(a => {
        if (a?.status !== 'completed') return false;
        const age = daysSince(a?.audit_date);
        return age !== null && age <= 30;
      });
      if (!completedRecently) {
        alerts?.push({
          id: `audit-overdue-${site?.id}`,
          type: 'overdue_inspection',
          severity: 'warning',
          title: 'No recent inspection',
          description: `${site?.site_name} has no completed inspection in the last 30 days.`,
          siteId: site?.id,
          siteName: site?.site_name,
          link: '/audits-inspections',
        });
      }
    });

    const severityOrder = { critical: 0, warning: 1, info: 2 };
    alerts?.sort((a, b) => (severityOrder?.[a?.severity] ?? 2) - (severityOrder?.[b?.severity] ?? 2));

    return alerts;
  },

  /**
   * Compute a compliance score (0-100) per site with risk level derivation
   * Risk level: High = score <50 or any critical alert, Medium = score 50-84 or any warning, Low = score >=85 no criticals
   */
  computeSiteScores({ sites, documents, snags, audits, tasks, incidents, alerts = [] }) {
    const today = new Date();
    today?.setHours(0, 0, 0, 0);

    const alertsBySite = {};
    (alerts || [])?.forEach(alert => {
      if (!alertsBySite?.[alert?.siteId]) alertsBySite[alert.siteId] = [];
      alertsBySite?.[alert?.siteId]?.push(alert);
    });

    return (sites || [])?.map(site => {
      const siteDocs = (documents || [])?.filter(d => d?.site_id === site?.id);
      const siteSnags = (snags || [])?.filter(s => s?.site_id === site?.id);
      const siteAudits = (audits || [])?.filter(a => a?.site_id === site?.id);
      const siteTasks = (tasks || [])?.filter(t => t?.site_id === site?.id);
      const siteIncidents = (incidents || [])?.filter(i => i?.site_id === site?.id);
      const siteAlerts = alertsBySite?.[site?.id] || [];

      let docScore = 40;
      if (siteDocs?.length === 0) {
        docScore = 0;
      } else {
        const expiredCount = siteDocs?.filter(d => {
          if (!d?.expiry_date) return false;
          return daysDiff(d?.expiry_date) < 0;
        })?.length;
        const missingExpiry = siteDocs?.filter(d => !d?.expiry_date)?.length;
        const siteCategories = new Set(
          siteDocs
            .filter(d => !d.expiry_date || daysDiff(d.expiry_date) >= 0)
            .map(d => d.category)
        );
        const missingCats = REQUIRED_CATEGORIES?.filter(c => !siteCategories?.has(c))?.length;
        const penalty = (expiredCount * 8) + (missingExpiry * 3) + (missingCats * 5);
        docScore = Math.max(0, 40 - penalty);
      }

      let inspectionScore = 25;
      const completedAudits = siteAudits?.filter(a => a?.status === 'completed');
      if (completedAudits?.length === 0) {
        inspectionScore = 0;
      } else {
        const latestAudit = completedAudits?.sort((a, b) => new Date(b.audit_date) - new Date(a.audit_date))?.[0];
        const age = daysSince(latestAudit?.audit_date);
        if (age > 60) inspectionScore = 5;
        else if (age > 30) inspectionScore = 15;
        else inspectionScore = 25;
      }

      let taskScore = 20;
      const requiredTasks = siteTasks?.filter(t => t?.required);
      if (requiredTasks?.length > 0) {
        const completedRequired = requiredTasks?.filter(t => t?.status === 'completed')?.length;
        taskScore = Math.round((completedRequired / requiredTasks?.length) * 20);
      }

      let incidentScore = 15;
      const criticalIncidents = siteIncidents?.filter(i => i?.severity_level === 'critical' || i?.severity_level === 'high')?.length;
      const incidentPenalty = criticalIncidents * 5;
      incidentScore = Math.max(0, 15 - incidentPenalty);

      const staleSnags = siteSnags?.filter(s => {
        if (s?.status === 'resolved') return false;
        const age = daysSince(s?.created_at);
        return age !== null && age > STALE_SNAG_DAYS;
      })?.length;
      const snagPenalty = Math.min(10, staleSnags * 2);

      const total = Math.max(0, Math.min(100, docScore + inspectionScore + taskScore + incidentScore - snagPenalty));

      const hasCriticalAlert = siteAlerts?.some(a => a?.severity === 'critical');
      const hasWarningAlert = siteAlerts?.some(a => a?.severity === 'warning');
      let riskLevel;
      if (total < 50 || hasCriticalAlert) {
        riskLevel = 'High';
      } else if (total < 85 || hasWarningAlert) {
        riskLevel = 'Medium';
      } else {
        riskLevel = 'Low';
      }

      return {
        siteId: site?.id,
        siteName: site?.site_name,
        score: total,
        breakdown: {
          documents: docScore,
          inspections: inspectionScore,
          tasks: taskScore,
          incidents: incidentScore,
          snagPenalty,
        },
        grade: total >= 85 ? 'A' : total >= 70 ? 'B' : total >= 50 ? 'C' : 'D',
        status: total >= 85 ? 'compliant' : total >= 50 ? 'warning' : 'critical',
        riskLevel,
      };
    });
  },
};
